import { useState, useEffect } from 'react';
import { X, Loader2 } from 'lucide-react';
import { scannerAPI } from '../../../services/scannerService';
import { ScannerDevice } from '../../../types/scanner';
import toast from 'react-hot-toast';

interface NewScanModalProps {
  device: ScannerDevice | null;
  onClose: () => void;
  onSuccess: () => void;
}

export default function NewScanModal({ device, onClose, onSuccess }: NewScanModalProps) {
  const [loading, setLoading] = useState(false);
  const [scanModes, setScanModes] = useState<string[]>([]);
  const [formData, setFormData] = useState({
    patient_id: '',
    patient_name: '',
    scan_mode: 'FA_100x',
    case_id: '',
  });

  if (!device) return null;

  useEffect(() => {
    loadScanModes();
  }, []);

  const loadScanModes = async () => {
    try {
      const modes = await scannerAPI.getScanModes(device.id);
      if (modes.success && modes.modes?.ModeList) {
        setScanModes(modes.modes.ModeList);
        if (modes.modes.ModeList.length > 0) {
          setFormData(prev => ({ ...prev, scan_mode: modes.modes.ModeList[0] }));
        }
      }
    } catch (error) {
      console.error('Failed to load scan modes:', error);
      // Use default modes if API fails
      setScanModes(['FA_100x', 'BF_40x', 'FA_40x']);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.patient_id && !formData.patient_name) {
      toast.error('Please enter patient ID or name');
      return;
    }

    setLoading(true);

    try {
      const result = await scannerAPI.createJob({
        device_id: device.id,
        patient_id: formData.patient_id,
        patient_name: formData.patient_name,
        scan_mode: formData.scan_mode,
        case_id: formData.case_id ? parseInt(formData.case_id) : undefined,
      });

      if (result.success) {
        toast.success('Scan started successfully!');
        onSuccess();
      } else {
        toast.error(result.error || 'Failed to start scan');
      }
    } catch (error: any) {
      console.error('Failed to start scan:', error);
      toast.error(error.message || 'Failed to start scan');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-900">Start New Scan</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition"
            disabled={loading}
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Device Info */}
          <div className="bg-blue-50 p-4 rounded-lg">
            <div className="text-sm text-gray-600">Scanner Device</div>
            <div className="font-semibold text-gray-900">{device.name}</div>
            <div className="text-xs text-gray-500">{device.machine_id}</div>
          </div>

          {/* Patient ID */}
          <div>
            <label htmlFor="patient_id" className="block text-sm font-medium text-gray-700 mb-1">
              Patient ID
            </label>
            <input
              type="text"
              id="patient_id"
              name="patient_id"
              value={formData.patient_id}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="e.g., P123456"
              disabled={loading}
            />
          </div>

          {/* Patient Name */}
          <div>
            <label htmlFor="patient_name" className="block text-sm font-medium text-gray-700 mb-1">
              Patient Name
            </label>
            <input
              type="text"
              id="patient_name"
              name="patient_name"
              value={formData.patient_name}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="e.g., Nguyen Van A"
              disabled={loading}
            />
          </div>

          {/* Scan Mode */}
          <div>
            <label htmlFor="scan_mode" className="block text-sm font-medium text-gray-700 mb-1">
              Scan Mode
            </label>
            <select
              id="scan_mode"
              name="scan_mode"
              value={formData.scan_mode}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={loading}
            >
              {scanModes.length > 0 ? (
                scanModes.map(mode => (
                  <option key={mode} value={mode}>{mode}</option>
                ))
              ) : (
                <>
                  <option value="FA_100x">FA_100x (Fluorescence 100x)</option>
                  <option value="BF_40x">BF_40x (Bright Field 40x)</option>
                  <option value="FA_40x">FA_40x (Fluorescence 40x)</option>
                </>
              )}
            </select>
          </div>

          {/* Case ID (optional) */}
          <div>
            <label htmlFor="case_id" className="block text-sm font-medium text-gray-700 mb-1">
              Case ID <span className="text-gray-400 text-xs">(optional)</span>
            </label>
            <input
              type="number"
              id="case_id"
              name="case_id"
              value={formData.case_id}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="e.g., 42"
              disabled={loading}
            />
          </div>

          {/* Buttons */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Starting...
                </>
              ) : (
                'Start Scan'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
